import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiArrowRight, FiChevronDown, FiCommand, FiGrid, FiSearch, FiSettings, FiX } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import SBSLogoMark from '../SBSLogoMark';
import { useWorkspaceExperience } from './WorkspaceExperience';
import { canOpenWorkspaceSettings, findWorkspaceCommands, workspaceArea } from './workspaceCommands';
import { commandLabel, workspaceCopy } from './workspaceCopy';
import './WorkspaceBar.css';

const recentKey = scope => 'sbs.workspace.recent.v1:' + scope;
function loadRecent(scope) {
  try { const value = JSON.parse(sessionStorage.getItem(recentKey(scope)) || '[]'); return Array.isArray(value) ? [...new Set(value.filter(id => typeof id === 'string' && /^[a-z]+$/.test(id)))].slice(0, 6) : []; } catch { return []; }
}

export default function WorkspaceBar() {
  const experience = useWorkspaceExperience();
  if (!experience?.active) return null;
  return <WorkspaceControls key={experience.scope} experience={experience} />;
}

function WorkspaceControls({ experience }) {
  const { commands, scope, companyName } = experience;
  const { userData } = useAuth() || {};
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language || 'en';
  const copy = key => workspaceCopy(language, key);
  const navigate = useNavigate();
  const location = useLocation();
  const area = workspaceArea(location.pathname);
  const [dialog, setDialog] = useState(null);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const [recent, setRecent] = useState(() => loadRecent(scope));
  const input = useRef(null);
  const panel = useRef(null);
  const returnFocus = useRef(null);
  const results = useRef([]);
  const currentWorkspace = commands.find(item => item.group === 'workspaces' && item.area === area);
  const suggestions = commands.filter(item => item.group === 'actions' && item.area === area).slice(0, 4);

  const open = mode => {
    if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
    returnFocus.current = document.activeElement;
    setQuery(''); setSelected(0); setDialog(mode);
  };
  const close = () => { setDialog(null); window.requestAnimationFrame(() => returnFocus.current?.isConnected && returnFocus.current.focus()); };

  useEffect(() => {
    const onShortcut = event => {
      if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        if (document.querySelector('[role="dialog"][aria-modal="true"]') && !panel.current) return;
        event.preventDefault();
        if (!dialog) { returnFocus.current = document.activeElement; setQuery(''); setSelected(0); setDialog('all'); }
      }
    };
    window.addEventListener('keydown', onShortcut);
    return () => window.removeEventListener('keydown', onShortcut);
  }, [dialog]);

  useEffect(() => { setDialog(null); setQuery(''); }, [location.pathname, location.search, location.hash]);
  useEffect(() => {
    if (!dialog) return undefined;
    input.current?.focus();
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = oldOverflow; };
  }, [dialog]);

  useEffect(() => {
    const route = location.pathname + location.search;
    const matching = commands.find(item => item.route === route);
    if (!matching) return;
    setRecent(current => {
      const next = [matching.id, ...current.filter(id => id !== matching.id)].slice(0, 6);
      try { sessionStorage.setItem(recentKey(scope), JSON.stringify(next)); } catch { /* Navigation works without browser storage. */ }
      return next.join('|') === current.join('|') ? current : next;
    });
  }, [scope, location.pathname, location.search, commands]);

  const groups = (() => {
    const pool = dialog === 'workspaces' ? commands.filter(item => item.group === 'workspaces') : commands;
    if (query.trim()) {
      const found = findWorkspaceCommands(pool, query);
      return ['actions', 'workspaces'].map(key => ({ key, items: found.filter(item => item.group === key) })).filter(group => group.items.length);
    }
    const result = [];
    const seen = new Set();
    const add = (key, items) => { const unique = items.filter(item => !seen.has(item.id)); unique.forEach(item => seen.add(item.id)); if (unique.length) result.push({ key, items: unique }); };
    if (dialog !== 'workspaces') {
      add('suggested', suggestions);
      add('recent', recent.map(id => pool.find(item => item.id === id)).filter(Boolean).slice(0, 4));
      if (!suggestions.length) add('actions', pool.filter(item => item.group === 'actions').slice(0, 5));
    }
    add('workspaces', pool.filter(item => item.group === 'workspaces'));
    return result;
  })();
  const flattened = groups.flatMap(group => group.items);
  const selectedIndex = Math.min(selected, Math.max(0, flattened.length - 1));

  const go = item => {
    setDialog(null); setQuery('');
    navigate(item.route);
  };
  const onDialogKey = event => {
    if (event.key === 'Escape') { event.preventDefault(); close(); }
    else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!flattened.length) return;
      const next = (selectedIndex + (event.key === 'ArrowDown' ? 1 : -1) + flattened.length) % flattened.length;
      setSelected(next); results.current[next]?.scrollIntoView?.({ block: 'nearest' });
      if (document.activeElement?.getAttribute('role') === 'option') results.current[next]?.focus();
    } else if (event.key === 'Enter' && (document.activeElement === input.current || document.activeElement?.getAttribute('role') === 'option') && flattened[selectedIndex]) { event.preventDefault(); go(flattened[selectedIndex]); }
    else if (event.key === 'Tab') {
      const elements = [...panel.current.querySelectorAll('button:not([disabled]), input, [tabindex="0"]')];
      const first = elements[0], last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
  };

  return <>
    <header className="sbs-workspace-bar" aria-label={copy('workspace')}>
      <button type="button" className="sbs-workspace-switch" onClick={() => open('workspaces')} aria-label={copy('switch')} aria-haspopup="dialog" aria-expanded={dialog === 'workspaces'}>
        <SBSLogoMark alt="" /><span><small>{companyName || 'Selenne Business Suite'}</small><strong>{currentWorkspace ? commandLabel(currentWorkspace, language) : copy('workspace')}</strong></span><FiChevronDown aria-hidden="true" />
      </button>
      <button type="button" className="sbs-workspace-search" onClick={() => open('all')} aria-haspopup="dialog" aria-expanded={dialog === 'all'}><FiSearch aria-hidden="true" /><span>{copy('search')}</span><kbd><FiCommand aria-hidden="true" /> K</kbd></button>
      <div className="sbs-workspace-bar-actions">
        {suggestions[0] && <button type="button" className="sbs-workspace-next" onClick={() => go(suggestions[0])}><span>{commandLabel(suggestions[0], language)}</span><FiArrowRight aria-hidden="true" /></button>}
        {canOpenWorkspaceSettings(userData) && <button type="button" className="sbs-workspace-settings" onClick={() => navigate('/settings')} aria-label={copy('settings')}><FiSettings aria-hidden="true" /></button>}
      </div>
    </header>
    {dialog && createPortal(<div className="sbs-command-dialog-scrim" onMouseDown={event => { if (event.target === event.currentTarget) close(); }}>
      <section ref={panel} className="sbs-command-dialog" role="dialog" aria-modal="true" aria-labelledby="sbs-command-title" onKeyDown={onDialogKey}>
        <header><div><p>Selenne Business Suite</p><h2 id="sbs-command-title">{copy(dialog === 'workspaces' ? 'switch' : 'title')}</h2></div><button type="button" onClick={close} aria-label={copy('close')}><FiX aria-hidden="true" /></button></header>
        <div className="sbs-command-search"><FiSearch aria-hidden="true" /><input ref={input} type="search" value={query} onChange={event => { setQuery(event.target.value); setSelected(0); }} placeholder={copy('placeholder')} aria-label={copy('search')} role="combobox" aria-autocomplete="list" aria-expanded="true" aria-controls="sbs-command-results" aria-activedescendant={flattened[selectedIndex] ? 'sbs-command-' + flattened[selectedIndex].id : undefined} /></div>
        <p className="sbs-command-result-status" role="status">{flattened.length} {copy('results')}</p>
        <div id="sbs-command-results" className="sbs-command-results" role="listbox" aria-label={copy('search')}>
          {!flattened.length && <div className="sbs-command-empty"><FiSearch aria-hidden="true" /><strong>{copy('none')}</strong><p>{copy('help')}</p></div>}
          {groups.map(group => <div key={group.key} role="group" aria-label={copy(group.key)}><h3>{copy(group.key)}</h3>{group.items.map(item => {
            const index = flattened.indexOf(item);
            return <button ref={node => { results.current[index] = node; }} type="button" role="option" aria-selected={selectedIndex === index} id={'sbs-command-' + item.id} key={item.id} className={selectedIndex === index ? 'is-selected' : ''} onFocus={() => setSelected(index)} onMouseEnter={() => setSelected(index)} onClick={() => go(item)}><span className="sbs-command-item-icon">{item.group === 'workspaces' ? <FiGrid aria-hidden="true" /> : <FiArrowRight aria-hidden="true" />}</span><span>{commandLabel(item, language)}</span><FiArrowRight className="sbs-command-open-icon" aria-hidden="true" /></button>;
          })}</div>)}
        </div>
        <footer><span>{copy('privacy')}</span><div><kbd>↑ ↓</kbd> {copy('navigate')} <kbd>↵</kbd> {copy('open')} <kbd>Esc</kbd> {copy('dismiss')}</div></footer>
      </section>
    </div>, document.body)}
  </>;
}
